import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import { useConciergeContext } from "../../ConciergeContextProvider.js";
import { useAppConfig } from "../../../api/cache/appConfigQueryOptions.js";
const isPhoneValid = (phone) => phone.replace(/\D/g, "").length >= 10;
function InlineLoginWidget({
  styles
}) {
  var _a;
  const {
    isSignedIn,
    inlineSignIn,
    inlineVerifyOtp
  } = useConciergeContext();
  const { data: appConfig } = useAppConfig();
  const appName = ((_a = appConfig == null ? void 0 : appConfig.domainConfig) == null ? void 0 : _a.brandName) || "Brandclub";
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const phoneInputRef = useRef(null);
  const otpInputRef = useRef(null);
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((v) => v - 1), 1e3);
    return () => clearTimeout(timer);
  }, [resendCooldown]);
  useEffect(() => {
    var _a2, _b;
    if (step === "phone") (_a2 = phoneInputRef.current) == null ? void 0 : _a2.focus();
    if (step === "otp") (_b = otpInputRef.current) == null ? void 0 : _b.focus();
  }, [step]);
  if (isSignedIn || step === "success") return null;
  const handleSubmitPhone = async (e) => {
    e == null ? void 0 : e.preventDefault();
    if (!inlineSignIn || loading) return;
    setError(null);
    setLoading(true);
    const result = await inlineSignIn(phone);
    setLoading(false);
    if ("error" in result) {
      setError(result.error);
    } else {
      setSession(result.session);
      setStep("otp");
      setResendCooldown(30);
    }
  };
  const handleVerifyOtp = async (e) => {
    e == null ? void 0 : e.preventDefault();
    if (!inlineVerifyOtp || loading) return;
    setError(null);
    setLoading(true);
    const result = await inlineVerifyOtp(session, otp);
    setLoading(false);
    if (result.success) {
      setStep("success");
    } else {
      setError(result.error);
    }
  };
  const handleResend = async () => {
    if (!inlineSignIn || resendCooldown > 0 || loading) return;
    setError(null);
    setLoading(true);
    const result = await inlineSignIn(phone);
    setLoading(false);
    if ("error" in result) {
      setError(result.error);
    } else {
      setSession(result.session);
      setResendCooldown(30);
      setOtp("");
    }
  };
  const disabled = loading;
  return /* @__PURE__ */ jsx("div", { style: { marginBottom: 16 }, children: /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        backgroundColor: styles.containerBg,
        borderRadius: 16,
        padding: "20px 20px",
        border: `1px solid ${styles.borderColor}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        width: "100%",
        boxSizing: "border-box"
      },
      children: [
        /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 10 }, children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: styles.iconBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              },
              children: /* @__PURE__ */ jsx(LockIcon, { color: styles.iconColor })
            }
          ),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("div", { style: { fontWeight: 700, fontSize: 15, color: styles.titleColor, lineHeight: 1.3 }, children: step === "otp" ? "Enter your verification code" : `Log in to your ${appName} account` }),
            /* @__PURE__ */ jsx("div", { style: { fontSize: 13, color: styles.subtitleColor, marginTop: 2, lineHeight: 1.4 }, children: step === "otp" ? `We sent a code to ${phone}` : "Enter your mobile number to sign in" })
          ] })
        ] }),
        step === "phone" && /* @__PURE__ */ jsxs("form", { onSubmit: handleSubmitPhone, style: { display: "flex", flexDirection: "column", gap: 10 }, children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              ref: phoneInputRef,
              type: "tel",
              placeholder: "Phone number",
              value: phone,
              onChange: (e) => {
                setPhone(e.target.value);
                setError(null);
              },
              disabled,
              style: inputStyle(styles, disabled)
            }
          ),
          error && /* @__PURE__ */ jsx("div", { style: errorStyle, children: error }),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "submit",
              disabled: disabled || !isPhoneValid(phone),
              style: buttonStyle(styles, disabled || !isPhoneValid(phone)),
              children: loading ? "Sending code…" : "Send Code"
            }
          )
        ] }),
        step === "otp" && /* @__PURE__ */ jsxs("form", { onSubmit: handleVerifyOtp, style: { display: "flex", flexDirection: "column", gap: 10 }, children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              ref: otpInputRef,
              type: "text",
              inputMode: "numeric",
              placeholder: "Verification code",
              value: otp,
              onChange: (e) => {
                setOtp(e.target.value.replace(/\D/g, ""));
                setError(null);
              },
              maxLength: 6,
              disabled,
              style: inputStyle(styles, disabled)
            }
          ),
          error && /* @__PURE__ */ jsx("div", { style: errorStyle, children: error }),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "submit",
              disabled: disabled || otp.length === 0,
              style: buttonStyle(styles, disabled || otp.length === 0),
              children: loading ? "Verifying…" : "Verify"
            }
          ),
          /* @__PURE__ */ jsx("div", { style: { textAlign: "center", fontSize: 13, color: styles.subtitleColor }, children: resendCooldown > 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
            "Resend code in ",
            resendCooldown,
            "s"
          ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
            "Didn't get a code?",
            " ",
            /* @__PURE__ */ jsx(
              "span",
              {
                onClick: handleResend,
                style: {
                  cursor: "pointer",
                  color: styles.linkColor,
                  fontWeight: 600,
                  textDecoration: "underline"
                },
                children: "Resend"
              }
            )
          ] }) }),
          /* @__PURE__ */ jsx("div", { style: { textAlign: "center" }, children: /* @__PURE__ */ jsx(
            "span",
            {
              onClick: () => {
                setStep("phone");
                setError(null);
                setOtp("");
              },
              style: {
                cursor: "pointer",
                fontSize: 12,
                color: styles.subtitleColor,
                textDecoration: "underline"
              },
              children: "Change phone number"
            }
          ) })
        ] })
      ]
    }
  ) });
}
function useInlineStyles() {
  return {
    containerBg: "var(--concierge-secondary-bg, #f8f9fa)",
    borderColor: "var(--concierge-border, #e5e7eb)",
    iconBg: "var(--concierge-primary, #181d56)",
    iconColor: "#ffffff",
    titleColor: "var(--concierge-text-primary, #111827)",
    subtitleColor: "var(--concierge-text-secondary, #6b7280)",
    inputBg: "#ffffff",
    inputBorder: "#d1d5db",
    inputFocusBorder: "var(--concierge-primary, #181d56)",
    buttonBg: "var(--concierge-primary, #181d56)",
    buttonText: "#ffffff",
    linkColor: "var(--concierge-primary, #181d56)"
  };
}
const errorStyle = {
  fontSize: 12,
  color: "#dc2626",
  padding: "4px 8px",
  backgroundColor: "#fef2f2",
  borderRadius: 6,
  lineHeight: 1.4
};
function inputStyle(styles, disabled) {
  return {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${styles.inputBorder}`,
    fontSize: 14,
    color: styles.titleColor,
    backgroundColor: disabled ? "#f9fafb" : styles.inputBg,
    outline: "none",
    boxSizing: "border-box",
    opacity: disabled ? 0.7 : 1,
    transition: "border-color 0.15s ease"
  };
}
function buttonStyle(styles, isDisabled) {
  return {
    width: "100%",
    padding: "10px 16px",
    borderRadius: 10,
    border: "none",
    fontSize: 14,
    fontWeight: 600,
    backgroundColor: styles.buttonBg,
    color: styles.buttonText,
    cursor: isDisabled ? "default" : "pointer",
    opacity: isDisabled ? 0.5 : 1,
    transition: "opacity 0.15s ease",
    letterSpacing: "0.01em",
    pointerEvents: isDisabled ? "none" : "auto"
  };
}
const LockIcon = ({ color }) => /* @__PURE__ */ jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [
  /* @__PURE__ */ jsx("rect", { x: "3", y: "11", width: "18", height: "11", rx: "2", ry: "2" }),
  /* @__PURE__ */ jsx("path", { d: "M7 11V7a5 5 0 0 1 10 0v4" })
] });
function PromptLoginPart({ reason: _reason }) {
  const { isSignedIn } = useConciergeContext();
  const styles = useInlineStyles();
  const isDevMode = new URLSearchParams(window.location.search).get("dev") === "true" || localStorage.getItem("devMode") === "true";
  if (isSignedIn || !isDevMode) return null;
  return /* @__PURE__ */ jsx(InlineLoginWidget, { styles });
}
export {
  PromptLoginPart
};
//# sourceMappingURL=PromptLoginPart.js.map
